// ./01 - PROJETOS DEV/meu-controle-de-estoque/src/components/views/DashboardFinanceiroView.jsx
import React, { useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { formatCurrency } from '../../utils/formatters';
import 'chart.js/auto';

const DashboardFinanceiroView = ({ 
    treasuryData, 
    openModal,
    selectedIds = [], 
    onSelectOne, 
    onSelectAll, 
    onDeleteSelected 
}) => {
    // Agora o horizon suporta strings para identificar Passado ou Futuro
    const [horizon, setHorizon] = useState('future_6');
    const [projectionType, setProjectionType] = useState('projected'); // 'projected' (Verde) ou 'realized' (Azul)

    // Filtra a linha do tempo baseada na seleção do usuário usando os marcadores virtuais criados no Hook
    const visibleTimeline = useMemo(() => {
        if (!treasuryData || !treasuryData.projectedTimeline) return [];
        const tl = treasuryData.projectedTimeline;

        switch (horizon) {
            case 'past_6': return tl.filter(t => t.isPast || t.isCurrent).slice(-6);
            case 'past_12': return tl.filter(t => t.isPast || t.isCurrent).slice(-12);
            case 'past_24': return tl.filter(t => t.isPast || t.isCurrent).slice(-24);
            case 'future_6': return tl.filter(t => t.isCurrent || t.isFuture).slice(0, 6);
            case 'future_12': return tl.filter(t => t.isCurrent || t.isFuture).slice(0, 12);
            case 'future_24': return tl.filter(t => t.isCurrent || t.isFuture).slice(0, 24);
            case 'future_36': return tl.filter(t => t.isCurrent || t.isFuture).slice(0, 36);
            case 'future_60': return tl.filter(t => t.isCurrent || t.isFuture).slice(0, 60);
            default: return tl.filter(t => t.isCurrent || t.isFuture).slice(0, 6);
        }
    }, [treasuryData, horizon]);

    const chartData = {
        labels: visibleTimeline.map(t => t.label),
        datasets: [
            {
                label: projectionType === 'projected' ? 'Saldo Projetado (Pendente + Real)' : 'Saldo Realizado (Apenas Confirmados)',
                data: visibleTimeline.map(t => projectionType === 'projected' ? t.projectedBalance : t.realizedBalance),
                borderColor: projectionType === 'projected' ? '#10B981' : '#3B82F6', 
                backgroundColor: projectionType === 'projected' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                borderWidth: 3,
                pointBackgroundColor: visibleTimeline.map(t => {
                    const val = projectionType === 'projected' ? t.projectedBalance : t.realizedBalance;
                    return val >= 0 ? (projectionType === 'projected' ? '#10B981' : '#3B82F6') : '#EF4444';
                }),
                pointBorderColor: '#fff',
                pointRadius: visibleTimeline.length <= 12 ? 6 : 3, // Ajusta o tamanho da bolinha se houver muitos meses
                fill: true,
                tension: 0.4 
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { 
                display: true,
                position: 'bottom',
                labels: { boxWidth: 12, font: { size: 12, weight: '600' } }
            },
            tooltip: {
                callbacks: {
                    label: (context) => `Saldo: ${formatCurrency(context.raw)}`
                }
            }
        },
        scales: {
            y: {
                ticks: { callback: value => formatCurrency(value) },
                grid: { 
                    color: (context) => context.tick.value === 0 ? '#000000' : '#e5e7eb', 
                    lineWidth: (context) => context.tick.value === 0 ? 2 : 1 
                }
            }
        }
    };

    const isAllSelected = treasuryData.adjustments.length > 0 && treasuryData.adjustments.every(adj => selectedIds.includes(adj.id));

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 className="text-2xl font-bold text-gray-800">Controle de Caixa e Tesouraria</h2>
                <button onClick={() => openModal('addTreasuryAdjustment')} className="custom-button py-2 px-6 font-semibold rounded-lg shadow-sm">
                    + Ajuste de Caixa (Manual)
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-center">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Saldo Atual em Caixa</h3>
                    <p className={`text-4xl font-black mt-2 ${treasuryData.currentBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(treasuryData.currentBalance)}
                    </p>
                </div>

                <div className="bg-white p-6 rounded-xl border border-blue-100 shadow-sm">
                    <h3 className="text-sm font-semibold text-blue-600 uppercase tracking-wider">Entradas Reais</h3>
                    <p className="text-2xl font-bold text-gray-800 mt-2">{formatCurrency(treasuryData.totalInflows)}</p>
                    <p className="text-xs text-gray-500 mt-1">Total recebido até hoje</p>
                </div>

                <div className="bg-white p-6 rounded-xl border border-red-100 shadow-sm">
                    <h3 className="text-sm font-semibold text-red-600 uppercase tracking-wider">Saídas Reais</h3>
                    <p className="text-2xl font-bold text-gray-800 mt-2">{formatCurrency(treasuryData.totalOutflows)}</p>
                    <p className="text-xs text-gray-500 mt-1">Total pago até hoje</p>
                </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h3 className="text-lg font-bold text-gray-800">Fluxo de Caixa Acumulado</h3>
                        <p className="text-sm text-gray-500">Visualize a saúde financeira e o histórico baseado nas liquidações.</p>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <div className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg border border-gray-200">
                            <label className="text-sm font-medium text-gray-600 whitespace-nowrap">Cenário:</label>
                            <select 
                                value={projectionType} 
                                onChange={(e) => setProjectionType(e.target.value)}
                                className="bg-transparent text-sm font-semibold text-gray-800 focus:outline-none cursor-pointer"
                            >
                                <option value="projected">Projetado (Pendente + Real)</option>
                                <option value="realized">Realizado (Apenas Caixa Real)</option>
                            </select>
                        </div>
                        
                        <div className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg border border-gray-200">
                            <label className="text-sm font-medium text-gray-600 whitespace-nowrap">Visão:</label>
                            <select 
                                value={horizon} 
                                onChange={(e) => setHorizon(e.target.value)}
                                className="bg-transparent text-sm font-semibold text-gray-800 focus:outline-none cursor-pointer"
                            >
                                <optgroup label="Histórico (Passado)">
                                    <option value="past_6">Últimos 6 Meses</option>
                                    <option value="past_12">Último 1 Ano</option>
                                    <option value="past_24">Últimos 2 Anos</option>
                                </optgroup>
                                <optgroup label="Projeções (Futuro)">
                                    <option value="future_6">Próximos 6 Meses</option>
                                    <option value="future_12">Próximos 12 Meses</option>
                                    <option value="future_24">Próximos 2 Anos</option>
                                    <option value="future_36">Próximos 3 Anos</option>
                                    <option value="future_60">Próximos 5 Anos</option>
                                </optgroup>
                            </select>
                        </div>
                    </div>
                </div>
                
                <div className="h-80">
                    <Line data={chartData} options={chartOptions} />
                </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Histórico de Ajustes Manuais</h3>
                
                {selectedIds && selectedIds.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex justify-between items-center mb-4">
                        <span className="text-sm font-medium text-blue-800">
                            {selectedIds.length} {selectedIds.length > 1 ? 'ajustes selecionados' : 'ajuste selecionado'}
                        </span>
                        <button onClick={onDeleteSelected} className="flex items-center gap-2 text-sm font-semibold text-red-600 hover:text-red-800">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            Excluir Selecionados
                        </button>
                    </div>
                )}

                {treasuryData.adjustments.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead className="border-b-2 border-gray-200">
                                <tr>
                                    <th className="p-4 w-4">
                                        <input 
                                            type="checkbox" 
                                            className="h-4 w-4 rounded border-gray-300" 
                                            checked={isAllSelected} 
                                            onChange={() => onSelectAll(treasuryData.adjustments.map(a => a.id), isAllSelected)} 
                                        />
                                    </th>
                                    <th className="p-4 text-sm font-semibold">Data</th>
                                    <th className="p-4 text-sm font-semibold">Tipo</th>
                                    <th className="p-4 text-sm font-semibold">Descrição</th>
                                    <th className="p-4 text-sm font-semibold">Valor</th>
                                    <th className="p-4 text-sm font-semibold text-center">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {treasuryData.adjustments.map(adj => (
                                    <tr key={adj.id} className="border-b border-gray-100 hover:bg-gray-50">
                                        <td className="p-4">
                                            <input 
                                                type="checkbox" 
                                                className="h-4 w-4 rounded border-gray-300" 
                                                checked={selectedIds?.includes(adj.id)} 
                                                onChange={() => onSelectOne(adj.id)} 
                                            />
                                        </td>
                                        <td className="p-4 text-gray-600">{adj.date.toLocaleDateString('pt-BR')}</td>
                                        <td className="p-4 font-medium">
                                            <span className={`px-2 py-1 rounded-full text-xs ${adj.type === 'Entrada' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {adj.type}
                                            </span>
                                        </td>
                                        <td className="p-4 text-gray-800">{adj.description}</td>
                                        <td className={`p-4 font-bold ${adj.type === 'Entrada' ? 'text-green-600' : 'text-red-600'}`}>
                                            {adj.type === 'Entrada' ? '+' : '-'}{formatCurrency(adj.amount)}
                                        </td>
                                        <td className="p-4 text-center">
                                            <div className="flex items-center justify-center gap-2">
                                                <button className="action-btn text-xl" onClick={() => openModal('editTreasuryAdjustment', adj)} title="Editar Ajuste">✏️</button>
                                                <button className="action-btn delete-btn text-xl" onClick={() => openModal('confirmDelete', { id: adj.id, type: 'treasuryAdjustment', name: adj.description })} title="Apagar Ajuste">🗑️</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-center text-gray-500 py-6">Nenhum ajuste manual registrado ainda.</p>
                )}
            </div>
        </div>
    );
};

export default DashboardFinanceiroView;